import React from 'react';
import { CoachAvailabilityExceptionInput } from 'globalTypes';

import TimezoneHelper from 'lib/timezones';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
import Checkbox from 'components/Checkbox';
import Container from 'components/Container';
import DatePickerInput from 'components/DatePickerInput';
import Heading from 'components/Heading';
import Icon from 'components/Icon';
import Label from 'components/Label';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import TimePickerInput from 'components/TimePickerInput';
import ToastActions from 'actions/ToastActions';
import EventTracking from 'lib/EventTracking';

type Props = {
  weekOffset: number;
  onAddTimeOff: (
    input: CoachAvailabilityExceptionInput[],
    timezone: string,
    onMutate?: () => void
  ) => void;
  onHide: () => void;
};

type State = {
  startDate: Date;
  endDate: Date;
  allDay: boolean;
};

export default class CoachingAvailabilityAddTimeOffView extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      startDate: this.getDefaultDate(props.weekOffset),
      endDate: this.getDefaultDate(props.weekOffset),
      allDay: false,
    };
  }

  componentDidMount() {
    EventTracking.trackKey('coaching.add_time_off.view');
  }

  getDefaultDate = (weekOffset: number) => {
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + weekOffset * 7);
    defaultDate.setMinutes(0);
    return defaultDate;
  };

  setStartDate = (startDate?: Date) => {
    if (startDate) {
      this.setState({ startDate }, () => {
        const newStartDateTime = startDate.getTime();
        if (this.state.endDate?.getTime() < newStartDateTime) {
          // if the end date is before the new start date,
          // modify the end date to equal the new start date
          const newEndDate = this.state.endDate;
          newEndDate.setTime(newStartDateTime);
          this.setState({ endDate: newEndDate });
        }
      });
    } else {
      ToastActions.showDefaultErrorToast();
    }
  };

  setEndDate = (endDate?: Date) => {
    if (endDate) {
      this.setState({ endDate }, () => {
        const newEndDateTime = endDate.getTime();
        if (endDate.getTime() < this.state.startDate.getTime()) {
          // if the end date is before the new start date,
          // modify the end date to equal the new start date
          const newStartDate = this.state.startDate;
          newStartDate.setTime(newEndDateTime);
          this.setState({ startDate: newStartDate });
        }
      });
    } else {
      ToastActions.showDefaultErrorToast();
    }
  };

  setStartTime = (startTime?: Date) => {
    if (startTime) {
      const { startDate, endDate } = this.state;
      const newStartDateTime = startTime.getTime();
      startDate.setTime(newStartDateTime);

      this.setState({ startDate }, () => {
        if (endDate.getTime() < newStartDateTime) {
          // if the new start time is later than the end time,
          // modify the end time to equal the new start time
          endDate.setTime(newStartDateTime);
          this.setState({ endDate });
        }
      });
    } else {
      ToastActions.showDefaultErrorToast();
    }
  };

  setEndTime = (endTime?: Date) => {
    if (endTime) {
      const { startDate, endDate } = this.state;
      const newEndDateTime = endTime.getTime();
      endDate.setTime(newEndDateTime);

      this.setState({ endDate }, () => {
        if (startDate.getTime() > newEndDateTime) {
          // if the start time is later than the new end time,
          // modify the start time to equal the new end time
          startDate.setTime(newEndDateTime);
          this.setState({ startDate });
        }
      });
    } else {
      ToastActions.showDefaultErrorToast();
    }
  };

  toggleAllDay = () => {
    this.setState({ allDay: !this.state.allDay }, () => {
      if (this.state.allDay) {
        // if toggling `allDay` on, then set the start and
        // end times to 00:00 and 23:00 accordingly
        const { startDate, endDate } = this.state;
        startDate.setHours(0);
        endDate.setHours(23);
        this.setState({ startDate, endDate });
      }
    });
  };

  handleSaveTimeOff = () => {
    const input = [
      {
        startDateTime: this.state.startDate.toISOString(),
        endDateTime: this.state.endDate.toISOString(),
      },
    ];
    this.props.onAddTimeOff(
      input,
      TimezoneHelper.userTimezoneDefaultValue ?? '',
      this.props.onHide
    );
  };

  renderStartDatePicker(isAllDay: boolean) {
    return (
      <DatePickerInput
        full
        format="MMMM d"
        value={this.state.startDate}
        min={new Date()}
        onChange={(date?: Date) => {
          this.setStartDate(date);
          isAllDay
            ? EventTracking.trackKey('coaching.add_time_off.select_all_day_start_date.click')
            : EventTracking.trackKey('coaching.add_time_off.select_start_date.click');
        }}
      />
    );
  }
  renderEndDatePicker(isAllDay: boolean) {
    return (
      <DatePickerInput
        full
        format="MMMM d"
        value={this.state.endDate}
        min={this.state.startDate}
        onChange={(date?: Date) => {
          this.setEndDate(date);
          isAllDay
            ? EventTracking.trackKey('coaching.add_time_off.select_all_day_end_date.click')
            : EventTracking.trackKey('coaching.add_time_off.select_end_date.click');
        }}
      />
    );
  }

  render() {
    const { allDay } = this.state;

    return (
      <Container size="xsmall">
        <MediaObject figure="right">
          <Heading serif>Add time off</Heading>
          <Link onClick={this.props.onHide}>
            <Icon name="x" color="pitch" />
          </Link>
        </MediaObject>
        <Text element="p" lineHeight="large">
          Add a one-off block of time for events like appointments or vacations when you’re
          unavailable during your normal availability.
        </Text>
        <Row>
          <Checkbox
            onChange={() => {
              this.toggleAllDay();
              EventTracking.trackKey('coaching.add_time_off.all_day.toggle');
            }}
            label="All day"
          />
        </Row>
        <Row size="small">
          <Label>Starts</Label>
          {allDay ? (
            this.renderStartDatePicker(allDay)
          ) : (
            <MediaObject figure="right" center gutter="small">
              {this.renderStartDatePicker(allDay)}
              <TimePickerInput
                step={60}
                value={this.state.startDate}
                onChange={(date?: Date) => {
                  this.setStartTime(date);
                  EventTracking.trackKey('coaching.add_time_off.select_start_time.click');
                }}
              />
            </MediaObject>
          )}
        </Row>
        <Row>
          <Label>Ends</Label>
          {allDay ? (
            this.renderEndDatePicker(allDay)
          ) : (
            <MediaObject figure="right" center gutter="small">
              {this.renderEndDatePicker(allDay)}
              <TimePickerInput
                step={60}
                value={this.state.endDate}
                min={this.state.startDate}
                onChange={(date?: Date) => {
                  this.setEndTime(date);
                  EventTracking.trackKey('coaching.add_time_off.select_end_time.click');
                }}
              />
            </MediaObject>
          )}
        </Row>
        <Buttons full>
          <Button
            data-test="set-availability-button"
            onClick={this.handleSaveTimeOff}
            eventKey="coaching.add_time_off.add_time_off.click">
            Add time off
          </Button>
        </Buttons>
      </Container>
    );
  }
}
